// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { type ReactNode } from "react";

import { useAutoScroll } from "../../utils/useAutoScroll";
import { TextSelectionToolbar } from "../transport/TextSelectionToolbar";

type AskV2ConversationShellProps = {
  testId: string;
  header?: ReactNode;
  tabs?: ReactNode;
  /** Omitted when `fullBleed` — composer lives inside the review workspace left column. */
  composer?: ReactNode;
  /** Review workspace fills the content column edge-to-edge (no 720px cap). */
  fullBleed?: boolean;
  rail?: ReactNode;
  children: ReactNode;
  onAddQuotedContext?: (input: {
    text: string;
    sourceMessageId?: string;
  }) => void;
  /** When set with scrollDeps, pins the thread to the latest messages on load. */
  scrollResetKey?: unknown;
  scrollDeps?: readonly unknown[];
  autoScrollEnabled?: boolean;
};

export function AskV2ConversationShell({
  testId,
  header,
  tabs,
  composer,
  fullBleed = false,
  rail,
  children,
  onAddQuotedContext,
  scrollResetKey,
  scrollDeps,
  autoScrollEnabled = true,
}: AskV2ConversationShellProps) {
  const autoScrollActive = scrollDeps != null;
  const { scrollContainerRef, contentRef, handleScroll } = useAutoScroll(
    scrollDeps ? [...scrollDeps] : [],
    scrollResetKey,
    { enabled: autoScrollActive && autoScrollEnabled },
  );

  return (
    <div
      data-testid={testId}
      className="ask-puntt-surface relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    >
      {header}
      {tabs}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {fullBleed ? (
            <div
              className="relative flex min-h-0 flex-1 overflow-hidden"
              data-testid="ask-v2-messages"
            >
              {children}
            </div>
          ) : (
            <>
              <div
                ref={scrollContainerRef}
                onScroll={autoScrollActive ? handleScroll : undefined}
                className="relative flex-1 overflow-y-auto px-4 py-6"
                data-testid="ask-v2-messages"
              >
                {onAddQuotedContext ? (
                  <TextSelectionToolbar
                    containerRef={scrollContainerRef}
                    onAddContext={onAddQuotedContext}
                  />
                ) : null}
                <div
                  ref={contentRef}
                  className="mx-auto w-full max-w-[720px] space-y-4"
                >
                  {children}
                </div>
              </div>
              {composer ? (
                <div
                  className="shrink-0 p-4"
                  data-testid="ask-v2-composer-region"
                >
                  <div className="mx-auto w-full max-w-[720px]">{composer}</div>
                </div>
              ) : null}
            </>
          )}
        </div>
        {rail}
      </div>
    </div>
  );
}
